import { useDispatch } from 'react-redux';
import { pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { Option } from 'fp-ts/Option';
import { Button, Stack } from '@optimus-web/core';
import { createZodFormResolver, LocalizedText, Form, FormProps } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { WithBillId } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import { Bill } from '@mews-commander/data-access';
import { navigateInModal } from '../../app';
import { findCounterByBillType } from '../../counters/helpers';
import { useGetCreditNoteCounters, useIsCreditNoteCountersEnabled } from '../../counters/utils';
import { ChangeBillingDetailsFormValues } from './types';
import { ChangeAssociatedAccountDetailsFields } from './ChangeAssociatedAccountDetailsFields';
import { ChangeOwnerDetailsFields } from './ChangeOwnerDetailsFields';
import { CreditNoteCounterSection } from './CreditNoteCounterSection';
import { changeBillingAddressSchema } from './schema';

type Props = WithBillId &
    Omit<FormProps<ChangeBillingDetailsFormValues>, 'children'> & {
        allowCustomerBillAddressObjection: boolean;
        billData: Option<Bill>;
    };

export const ChangeBillingDetailsForm = ({
    allowCustomerBillAddressObjection,
    onSubmit,
    billId,
    defaultValues,
    billData,
}: Props) => {
    const dispatch = useDispatch();
    const resolveRoute = useResolveRoute();

    const handleCancelClick = () => {
        const billDetailUrl = resolveRoute(RouteName.BillDetail, { id: billId });
        dispatch(navigateInModal(billDetailUrl));
    };

    const showCreditNoteCounterSelect = useIsCreditNoteCountersEnabled();
    const creditNoteCounters = useGetCreditNoteCounters();
    const billType = pipe(
        billData,
        option.map((it) => it.type)
    );
    const hasCreditNoteCounters = pipe(creditNoteCounters, array.some(findCounterByBillType(billType)));

    const isDisabled = (hasDirtyFields: boolean) =>
        !hasDirtyFields || (showCreditNoteCounterSelect && !hasCreditNoteCounters);

    return (
        <Form<ChangeBillingDetailsFormValues>
            resolver={createZodFormResolver(changeBillingAddressSchema(showCreditNoteCounterSelect))}
            onSubmit={onSubmit}
            defaultValues={defaultValues}
        >
            {({ hasDirtyFields, submit }) => (
                <Stack spacing="200" vertical>
                    <ChangeOwnerDetailsFields allowCustomerBillAddressObjection={allowCustomerBillAddressObjection} />
                    {showCreditNoteCounterSelect ? <CreditNoteCounterSection billType={billType} /> : null}
                    <ChangeAssociatedAccountDetailsFields />
                    <Stack alignment="end" spacing="200">
                        <Button onClick={handleCancelClick} size="medium" variant="secondary" type="submit">
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                        <Button
                            variant="primary"
                            onClick={submit}
                            disabled={isDisabled(hasDirtyFields)}
                            size="medium"
                            type="submit"
                        >
                            <LocalizedText textKey={TextKey.ChangeBillingDetailsSubmitButtonLabel} />
                        </Button>
                    </Stack>
                </Stack>
            )}
        </Form>
    );
};
